import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  AvatarColors,
  AvatarSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { ROUTES } from '@/app/routes';
import { useTranslations, useDismissOnOutside } from '@/hooks';
import { useLogoutMutation } from '@/slices/auth/api/authApi';
import { loggedOut } from '@/slices/auth/constants/sessionSlice';
import { selectCurrentUser } from '@/slices/auth/constants/sessionSelectors';
import styles from './UserMenu.module.scss';

// Avatar trigger + profile dropdown; sign out is the only live action.
export const UserMenu = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const [logout, { isLoading }] = useLogoutMutation();

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((wasOpen) => !wasOpen), []);

  useDismissOnOutside(rootRef, open, close);

  const handleProfileSettings = useCallback(() => {
    // TODO: navigate to the profile screen once it exists.
    close();
  }, [close]);

  const handleSignOut = useCallback(async () => {
    // `finally`: a failed request must never strand the user signed in.
    try {
      await logout().unwrap();
    } finally {
      dispatch(loggedOut());
      close();
      navigate(ROUTES.login, { replace: true });
    }
  }, [logout, dispatch, close, navigate]);

  const displayName = user?.name ?? '';

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={t('NAV_USER_MENU')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <Avatar name={displayName} color={AvatarColors.success} size={AvatarSizes.md} />
      </button>

      {open && (
        <div className={styles.panel} role="menu">
          <div className={styles.identity}>
            <Avatar name={displayName} color={AvatarColors.success} size={AvatarSizes.md} />
            <div className={styles.identityText}>
              <Typography
                as={TypographyElements.span}
                variant={TypographyVariants.label}
                weight={TypographyWeights.semibold}
                color={TypographyColors.onBrand}
              >
                {displayName}
              </Typography>
              <Typography
                as={TypographyElements.span}
                variant={TypographyVariants.caption}
                className={styles.email}
              >
                {user?.email ?? ''}
              </Typography>
            </div>
          </div>

          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={handleProfileSettings}
          >
            <Typography
              as={TypographyElements.span}
              variant={TypographyVariants.bodySm}
              color={TypographyColors.primary}
            >
              {t('NAV_PROFILE_SETTINGS')}
            </Typography>
          </button>

          <div className={styles.divider} />

          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={handleSignOut}
            disabled={isLoading}
          >
            <Typography
              as={TypographyElements.span}
              variant={TypographyVariants.bodySm}
              color={TypographyColors.error}
            >
              {t('NAV_SIGN_OUT')}
            </Typography>
          </button>
        </div>
      )}
    </div>
  );
};
